import client from './api/client.js'

/**
 * Payment link API — backed by com.avocash.paymentLink.controller.PaymentLinkController.
 *
 * Endpoints:
 *   POST payment-links                       create   (authenticated)
 *   GET  payment-links/{path}/{slug}          preview  (public)
 *   POST payment-links/{path}/{slug}/checkout createCheckoutSession (auth if link requires it)
 *
 * `path` is the URL bucket: "p" (reusable product/shop), "c" (campaign/group-buy
 * /save-to-buy) or "i" (private participant/installment/custom/order link).
 * e.g. https://pay.avocash.com/p/abc123 -> path="p", slug="abc123"
 *
 * Note: the payment-links controller lives at the backend root "/api/" (not
 * "/api/v1/"), so URLs are resolved against VITE_APP_URL using the same
 * `VITE_APP_URL + "api/..."` convention as the rest of the app.
 */
const base = `${import.meta.env.VITE_APP_URL}payment-links`

export const paymentLinkApi = {
  /** @param {object} payload CreatePaymentLinkRequest */
  create(payload) {
    return client.post(base, payload).then((r) => r.data)
  },

  /** @param {string} path one of "p" | "c" | "i" @param {string} slug */
  preview(path, slug) {
    return client
      .get(`${base}/${encodeURIComponent(path)}/${encodeURIComponent(slug)}`)
      .then((r) => r.data)
  },

  /** @param {string} path one of "p" | "c" | "i" @param {string} slug @param {object} payload CreateCheckoutRequest */
  checkout(path, slug, payload) {
    return client
      .post(`${base}/${encodeURIComponent(path)}/${encodeURIComponent(slug)}/checkout`, payload)
      .then((r) => r.data)
  },

  /**
   * Parse a payment-link share URL like https://pay.avocash.com/p/abc123
   * into { path, slug }. Returns null if the URL is not a payment-link URL.
   * @param {string} url
   */
  parsePath(url) {
    try {
      const { hostname, pathname } = new URL(url)
      if (!/^pay\.avocash\.com$/.test(hostname)) return null
      const segments = pathname.split('/').filter(Boolean)
      if (segments.length !== 2 || !/^[pci]$/.test(segments[0])) return null
      return { path: segments[0], slug: segments[1] }
    } catch (e) {
      return null
    }
  },
}
